import React, { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";

import {
  type ImportBrandsModalProps,
  type ParsedBrand,
  type ValidationResult,
  parseBrandCsvFile,
  downloadBrandSampleCSV,
  executeBrandImport,
  BrandImportUploadSection,
  BrandImportPreviewTable,
  BrandImportValidationResults,
  BrandImportModalFooter,
} from "./import";

export type { ImportBrandsModalProps, ParsedBrand, ValidationResult };

export function ImportBrandsModal({ open, onOpenChange, onImported }: ImportBrandsModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, unknown>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentOrganization } = useOrganization();

  const resetModal = useCallback(() => {
    setFile(null);
    setPreviewData([]);
    setHeaders([]);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    resetModal();
  }, [onOpenChange, resetModal]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);

    parseBrandCsvFile(
      selectedFile,
      ({ headers: parsedHeaders, previewData: parsedPreview, validation }) => {
        setHeaders(parsedHeaders);
        setPreviewData(parsedPreview);
        setValidationResult(validation);
      },
      (error) => {
        toast({
          title: "Error parsing CSV",
          description: error.message,
          variant: "destructive",
        });
      }
    );
  };

  const handleImport = async () => {
    if (!validationResult?.valid.length) {
      toast({
        title: "No valid data to import",
        description: "Please fix validation errors first.",
        variant: "destructive",
      });
      return;
    }

    if (!currentOrganization?.id) {
      toast({
        title: "Error",
        description: "No organization selected. Please select an organization first.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const success = await executeBrandImport({
        validBrands: validationResult.valid,
        organizationId: currentOrganization.id,
        onSuccess: () => {
          onImported();
          onOpenChange(false);
          resetModal();
        },
      });

      if (!success) {
        // executeBrandImport displays toast on failure
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Show loading or error state if no organization
  if (!currentOrganization) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Brands from CSV</DialogTitle>
          </DialogHeader>
          <div className="p-6 text-center">
            <p className="text-gray-600">Please select an organization first to import brands.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Brands from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* File Upload Section */}
          <BrandImportUploadSection
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onDownloadSample={downloadBrandSampleCSV}
          />

          {/* Preview Section */}
          <BrandImportPreviewTable headers={headers} previewData={previewData} />

          {/* Validation Results */}
          {validationResult && (
            <BrandImportValidationResults validationResult={validationResult} />
          )}

          {/* Action Buttons */}
          <BrandImportModalFooter
            onClose={handleClose}
            onImport={handleImport}
            validCount={validationResult?.valid.length || 0}
            isUploading={isUploading}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
